<template>
  <div class="v-code-display">
    <div class="v-code-display__header">
      <span class="v-code-display__language">{{ language }}</span>
      <button
        class="v-code-display__copy-btn"
        @click="copyCode"
        v-tooltip="'Copy to clipboard'"
        aria-label="Copy to clipboard"
      >
        <v-icon name="copy-content" size="xsmall"></v-icon>
      </button>
    </div>
    <div class="v-code-display__content" tabindex="0">
      <pre><code :class="`language-${language}`" v-html="highlightedCode"></code></pre>
    </div>
  </div>
</template>

<script>
import hljs from "highlight.js/lib/core";
import xml from "highlight.js/lib/languages/xml";
import javascript from "highlight.js/lib/languages/javascript";
import typescript from "highlight.js/lib/languages/typescript";
import json from "highlight.js/lib/languages/json";
import css from "highlight.js/lib/languages/css";
import scss from "highlight.js/lib/languages/scss";
import markdown from "highlight.js/lib/languages/markdown";
import bash from "highlight.js/lib/languages/bash";
import "highlight.js/styles/stackoverflow-dark.css";

// Register the languages
hljs.registerLanguage("xml", xml);
hljs.registerLanguage("html", xml); // HTML uses XML highlighter
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("js", javascript); // JS alias
hljs.registerLanguage("typescript", typescript);
hljs.registerLanguage("ts", typescript); // TS alias
hljs.registerLanguage("json", json);
hljs.registerLanguage("css", css);
hljs.registerLanguage("scss", scss);
hljs.registerLanguage("markdown", markdown);
hljs.registerLanguage("md", markdown); // MD alias
hljs.registerLanguage("bash", bash);
hljs.registerLanguage("shell", bash); // Shell alias

export default {
  name: "VCodeDisplay",
  props: {
    language: {
      type: String,
      default: "xml"
    },
    code: {
      type: String,
      default: ""
    }
  },
  computed: {
    highlightedCode() {
      if (!this.code) {
        return "";
      }

      try {
        // Use highlight.js to highlight the entire code block
        return hljs.highlight(this.code, {
          language: this.language,
          ignoreIllegals: true
        }).value;
      } catch (e) {
        console.error("Highlight.js error:", e);
        return this.escapeHtml(this.code);
      }
    }
  },
  methods: {
    escapeHtml(text) {
      // Escape HTML special characters to prevent XSS attacks incase of highlight.js failure
      return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    copyCode() {
      // Use the modern Clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        // Modern browsers in secure contexts
        navigator.clipboard
          .writeText(this.code)
          .then(() => {
            // Optional: Add visual feedback here
            console.log("Copied to clipboard successfully!");
          })
          .catch(err => {
            console.error("Could not copy text: ", err);
          });
      } else {
        // Fallback for older browsers or non-secure contexts
        try {
          const textArea = document.createElement("textarea");
          textArea.value = this.code;
          textArea.style.position = "fixed";
          textArea.style.opacity = "0";
          document.body.appendChild(textArea);
          textArea.select();

          const successful = document.execCommand("copy");
          document.body.removeChild(textArea);

          if (!successful) {
            console.error("Fallback: Unable to copy");
          }
        } catch (err) {
          console.error("Fallback: Unable to copy", err);
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.v-code-display {
  border-radius: 8px;
  overflow: hidden;
  font-family: "SFMono-Regular", Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  margin-bottom: 1.5rem;
  width: 100%;
  max-width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #21252b;
    padding: 8px 16px;
    position: relative;
    margin-block-end: -5px;
  }

  &__language {
    color: #939fb1;
    font-size: 0.8rem;
    text-transform: lowercase;
  }

  &__copy-btn {
    background: transparent;
    border: none;
    color: #939fb1;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #abb2bf;
    }

    &:focus {
      outline: none;
    }
  }

  &__content {
    padding: 0;
    overflow-x: auto;
    width: 100%;
    max-width: 100%;

    pre {
      margin: 0;
      padding: 16px;
      background-color: #282c34;
      border-radius: 6px;
    }

    code {
      background: transparent;
      white-space: pre;
      padding: 0;
      font-family: inherit;
      font-size: 0.85rem;
    }
  }
}
</style>
